import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { IconButton, Tooltip } from "@mui/material";
import numeral from "numeral";
import { useContext, useMemo } from "react";
import { ExploreContext } from "../context/explore";
import styles from "../styles/components/explore-plot-timeseries.module.scss";
import { ExploreSelect } from "./explore-select";

const enableCustomData = import.meta.env.VITE_ENABLE_CUSTOM_DATA === 'true'

interface Item {
  key: string;
  title: string;
}
interface ExploreTimeseriesPlotProps {
  currentKey?: string[] | null;
  onChangeKey: (key: string[] | null) => void;
}

export const ExploreSecondarySourceSelect = ({
  currentKey = null,
  onChangeKey,
}: ExploreTimeseriesPlotProps) => {

  const {
    exploreDataSchema,
    customDataPoints,
    normaliseTimeline, 
    setNormaliseTimeline,
    currentCustomPoint,
    setCurrentCustomPoint,
    secondaryDataSchema,
    linkSecondaryVectors,
    setLinkSecondaryVectors
  } = useContext(ExploreContext);
  
  const customDataPointsKeys = useMemo(() => {
    if (customDataPoints && Array.isArray(customDataPoints)) {
      return [{key: 'select', title: 'Select to compare'}, ...customDataPoints.map( point => {
        const {lat, lon, distance, id, name} = point
        return {
          key: `${id}`,
          title: `${(name ? `${name}` : `Unnamed custom data point`)} - dist: ${numeral(distance).format('0.0a') }m  
**Lat: ${numeral(lat).format('0.0000a')} Lon: ${numeral(lon).format('0.0000a')}**`
        }  

      })]
    }
    return []
  }, [customDataPoints])


  const columnSecondaryKeys: Item[] = useMemo(() => {
    let mainColumnItems: Item[] = []
    let mainColumnKeys: string[] = []

    if (exploreDataSchema !== null) {
      mainColumnItems = exploreDataSchema.vectors.map(({key, name}) => {
        return {key, title: name}
      })
      mainColumnKeys = exploreDataSchema.vectors.map(({key}) => key)
    }
    if (secondaryDataSchema !== null) {
      const secondaryItems = secondaryDataSchema.vectors.map(({key, name}) => {
        return {key, title: name}
      })
      const secondaryKeys = secondaryItems.map(({key}) => key)

      // merge keys so all new keys from secondary are added to the end
      // the keys that are in main column but are not in secondary are marked with dash
      const mergedKeys: Item[] = [
        ...mainColumnItems.map(({key, title}) => {
            return secondaryKeys.includes(key) ? {key, title} : {key, title: `~~${title}~~`}
          }
        ),
        ...secondaryItems.filter(({key}) => !mainColumnKeys.includes(key)).map(({key}) => {
          return {key, title: key}
        }),
      ];
      return mergedKeys;
    }
    return []
  }, [JSON.stringify(secondaryDataSchema), JSON.stringify(exploreDataSchema)])

  const useCurrentKey: number | undefined = useMemo(() => {
    const found = columnSecondaryKeys.findIndex( ({key}) => {
      if (currentKey && currentKey.length > 0) {
        return key === currentKey[0]
      }
      return false
    })
    if (found !== -1) {
      return found
    }
    return undefined
  }, [currentKey, columnSecondaryKeys])

  const initialValue = useMemo(() => {
    // find index
    if (currentCustomPoint && customDataPoints) {
      const found = customDataPoints.findIndex( p => `${p.id}` === `${currentCustomPoint.id}`)
      if (found !== -1) {
        return found + 1
      }
    }
    return 0
  }, [currentCustomPoint])

  return (
    <>
      {customDataPointsKeys.length > 1 &&
        <div className={styles.row}>
          {currentCustomPoint && 
            <>
              <Tooltip title="Sync up vector selection if matching names found">
                <IconButton
                  className={styles.iconButton}
                  onClick={ () => {
                    setLinkSecondaryVectors(i => !i)
                  }}
                >
                  {linkSecondaryVectors ? <LinkIcon color="primary" /> : <LinkOffIcon color="primary" />}
                </IconButton>
              </Tooltip>
              <ExploreSelect
                disabled={linkSecondaryVectors}
                initialValue={useCurrentKey}
                columnKeys={columnSecondaryKeys} 
                onChangeKey={(key: string[] | null) => {
                  onChangeKey(key)
                }}
              />
            </>
          }
          {(
            customDataPoints
            && currentCustomPoint !== null
            && customDataPoints.length > 0
          ) ?
            <Tooltip title="Clip timeline">
              <IconButton
                className={styles.iconButton}
                onClick={ () => {
                  setNormaliseTimeline(i => !i)
                }}
              >
                {normaliseTimeline ? <AccessTimeFilledIcon color="primary" /> : <AccessTimeIcon color="disabled" />}  
              </IconButton>
            </Tooltip>
          : 
            null
          }
          
          {enableCustomData && 
            <ExploreSelect 
              initialValue={initialValue}
              title="Custom data points"
              columnKeys={customDataPointsKeys} 
              onChangeKey={(key: string[] | null) => {
                if (key !== null && key[0] !== 'select') {
                  const [id] = key
                  if (customDataPoints) {
                    const found = customDataPoints.find( p => `${p.id}` === id)
                    if (found) {
                      setCurrentCustomPoint(found)
                    }
                  }
                } else if (currentCustomPoint) {
                  // setCurrentCustomPoint({...currentCustomPoint})
                } else {
                  setCurrentCustomPoint(null)
                }
            }} />
          }
          
        </div>
      }
    </>
  );
};
